import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
} from 'lucide-react';
import { meetingService } from '../../services/meeting.service.ts';
import CalendarMonthGrid from './components/CalendarMonthGrid.tsx';
import CalendarDayPanel from './components/CalendarDayPanel.tsx';
import GoogleCalendarConnect from './components/GoogleCalendarConnect.tsx';
import type { CalendarEvent, CalendarView } from './types/calendar.types.ts';
import {
  addDays,
  addMonths,
  buildPlanMeetingUrl,
  formatMonthYear,
  parseDateInputValue,
  startOfWeek,
  toDateKey,
} from './utils/calendarUtils.ts';
import {
  groupEventsByDate,
  meetingsToCalendarEvents,
} from './utils/meetingToCalendarEvent.ts';
import {
  readGoogleCalendarEventsCache,
  syncGoogleCalendarEvents,
} from './services/googleCalendar.service.ts';
import PageHeader, { pageHeaderPrimaryButtonClass } from '../dashboard/components/PageHeader.tsx';

const WEEKDAY_HEADERS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const CalendarPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [viewDate, setViewDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [view, setView] = useState<CalendarView>('month');
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const meetings = await meetingService.getMeetings();
      const tbkEvents = meetingsToCalendarEvents(
        meetings.filter((m) => m.status !== 'CANCELLED')
      );
      let googleEvents: CalendarEvent[] = [];
      try {
        googleEvents = await syncGoogleCalendarEvents();
      } catch {
        googleEvents = readGoogleCalendarEventsCache();
      }
      setEvents([...tbkEvents, ...googleEvents]);
    } catch (error) {
      console.error('Takvim yüklenemedi:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (!dateParam) return;
    const parsed = parseDateInputValue(dateParam);
    if (parsed) {
      setSelectedDate(parsed);
      setViewDate(parsed);
    }
  }, [searchParams]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  const eventsByDate = useMemo(() => groupEventsByDate(events), [events]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(viewDate, true);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [viewDate]);

  const goToday = () => {
    const now = new Date();
    setViewDate(now);
    setSelectedDate(now);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={CalendarDays}
        title="Takvim"
        description="Toplantılarınızı görüntüleyin ve Google Takvim ile senkronlayın."
        actions={
          <>
            <div className="flex rounded-lg border border-white/10 overflow-hidden">
              <button
                type="button"
                onClick={() => setView('month')}
                className={`px-3 py-1.5 text-sm ${
                  view === 'month' ? 'bg-purple-600 text-white' : 'bg-white/5 text-gray-400'
                }`}
              >
                Ay
              </button>
              <button
                type="button"
                onClick={() => setView('week')}
                className={`px-3 py-1.5 text-sm ${
                  view === 'week' ? 'bg-purple-600 text-white' : 'bg-white/5 text-gray-400'
                }`}
              >
                Hafta
              </button>
            </div>

            <button
              type="button"
              onClick={goToday}
              className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm"
            >
              Bugün
            </button>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() =>
                  setViewDate((d) => (view === 'month' ? addMonths(d, -1) : addDays(d, -7)))
                }
                className="p-2 rounded-lg bg-white/10 hover:bg-white/15 text-white"
                aria-label="Önceki"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="min-w-[140px] text-center text-white font-medium text-sm capitalize px-2">
                {view === 'month'
                  ? formatMonthYear(viewDate)
                  : `${weekDays[0].toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} – ${weekDays[6].toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`}
              </span>
              <button
                type="button"
                onClick={() =>
                  setViewDate((d) => (view === 'month' ? addMonths(d, 1) : addDays(d, 7)))
                }
                className="p-2 rounded-lg bg-white/10 hover:bg-white/15 text-white"
                aria-label="Sonraki"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <Link to={buildPlanMeetingUrl(selectedDate)} className={pageHeaderPrimaryButtonClass}>
              <Plus className="w-4 h-4" />
              Toplantı ekle
            </Link>
          </>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-400 gap-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          Takvim yükleniyor...
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4">
          <div>
            {view === 'month' ? (
              <CalendarMonthGrid
                viewDate={viewDate}
                selectedDate={selectedDate}
                eventsByDate={eventsByDate}
                onSelectDate={setSelectedDate}
                onSelectEvent={(event) => setSelectedDate(event.start)}
              />
            ) : (
              <div className="bg-slate-800/50 border border-white/10 rounded-xl overflow-hidden">
                <div className="grid grid-cols-7 border-b border-white/10 bg-slate-900/40">
                  {WEEKDAY_HEADERS.map((label, i) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setSelectedDate(weekDays[i])}
                      className={`py-2 text-center text-xs font-semibold ${
                        toDateKey(weekDays[i]) === toDateKey(selectedDate)
                          ? 'text-purple-300 bg-purple-600/20'
                          : 'text-gray-400'
                      }`}
                    >
                      <div>{label}</div>
                      <div className="text-white text-sm mt-0.5">{weekDays[i].getDate()}</div>
                    </button>
                  ))}
                </div>
                <div className="p-4 min-h-[320px]">
                  <CalendarDayPanel selectedDate={selectedDate} events={events} />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {view === 'month' && (
              <CalendarDayPanel selectedDate={selectedDate} events={events} />
            )}
            <GoogleCalendarConnect onSync={() => void loadEvents()} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
